import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Star, X, UserRound } from "lucide-react";
import { scrollToId, useLang } from "@/i18n";
import { useAuth } from "@/auth";

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 150);
    } else {
      scrollToId(id);
    }
  };

  const accessLabel = user ? (user.role === "admin" ? "Gestión" : user.role === "teacher" ? "Profesoras" : "Mi panel") : t.nav.acceso;
  const accessTarget = user ? (user.role === "admin" ? "/gestion" : user.role === "teacher" ? "/profesora" : "/alumno") : "/acceso";

  const enter = (path) => {
    setOpen(false);
    navigate(path);
  };

  const LangPill = () => (
    <div className="flex items-center rounded-full border border-white/15 bg-white/5 p-1 backdrop-blur-md">
      {["es", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}`}
          onClick={() => setLang(l)}
          className={`rounded-full px-3 py-1 font-mono text-xs font-bold uppercase tracking-widest transition-all duration-300 ${
            lang === l ? "bg-accent text-white" : "text-zinc-400 hover:text-white"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-ink/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <button
          data-testid="navbar-logo"
          onClick={() => enter("/")}
          className="flex items-center font-display text-lg font-black uppercase tracking-tight"
        >
          <img src="/logo-mark.webp" alt="Logo K-house Dance Academy" data-testid="navbar-logo-img" className="mr-2.5 h-10 w-auto" />
          K<span className="text-accent">-</span>house
          <span className="ml-2 hidden font-mono text-[10px] font-medium leading-tight tracking-[0.3em] text-zinc-500 2xl:inline">
            DANCE ACADEMY
          </span>
        </button>

        <nav className="hidden items-center gap-5 lg:flex xl:gap-6">
          {t.nav.links.map((l) => (
            <button
              key={l.id}
              data-testid={`navbar-link-${l.id}`}
              onClick={() => go(l.id)}
              className="whitespace-nowrap font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-400 transition-colors duration-300 hover:text-accent"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <LangPill />
          <button
            data-testid="navbar-workshop"
            onClick={() => enter("/workshop")}
            className="hidden items-center gap-1.5 rounded-full border-2 border-accent bg-accent/10 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-accent transition-all duration-300 hover:bg-accent hover:text-white 2xl:flex"
          >
            <Star size={12} /> Workshop JAY KIM
          </button>
          <button
            data-testid="navbar-access"
            onClick={() => enter(accessTarget)}
            className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:border-accent hover:text-accent"
          >
            <UserRound size={14} />
            {accessLabel}
          </button>
          <button
            data-testid="navbar-cta"
            onClick={() => go("contacto")}
            className="rounded-full bg-accent px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white shadow-[0_0_20px_rgba(255,0,122,0.4)] transition-all duration-300 hover:bg-accent-hover hover:shadow-[0_0_30px_rgba(255,0,122,0.6)]"
          >
            {t.nav.cta}
          </button>
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <LangPill />
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            className="rounded-full border border-white/15 bg-white/5 p-2.5"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden border-t border-white/10 bg-ink/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {t.nav.links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="py-2.5 text-left font-display text-xl font-bold uppercase text-zinc-200 transition-colors hover:text-accent"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-workshop"
                onClick={() => enter("/workshop")}
                className="mt-3 flex items-center justify-center gap-2 rounded-full border-2 border-accent py-3.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-accent"
              >
                <Star size={14} /> Workshop JAY KIM
              </button>
              <button
                data-testid="mobile-access"
                onClick={() => enter(accessTarget)}
                className="mt-2 rounded-full border border-white/20 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white"
              >
                {accessLabel}
              </button>
              <button
                data-testid="mobile-cta"
                onClick={() => go("contacto")}
                className="mt-2 rounded-full bg-accent py-3.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white"
              >
                {t.nav.cta}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
